import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { ConfirmDialog } from "../client-groups/ConfirmDialog";
import { DeliveryBadge, SendSampleButton } from "./DeliveryControls";

const GROUPS = [
  { label: "Plaintiff emails", match: (r) => r.audience === "plaintiff" },
  { label: "Attorney emails", match: (r) => r.audience === "attorney" },
  { label: "Internal team emails", match: (r) => r.audience === "internal" },
  { label: "Other templates", match: () => true },
];

const Row = ({ r, onFlip }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-100" data-testid={`delivery-row-${r.key}`}>
    <div className="min-w-0">
      <div className="text-sm font-medium text-slate-900">{r.name || r.key}</div>
      <div className="text-xs text-slate-500"><code>{r.key}</code>
        {r.kind === "direct" && <span className="ml-2 text-slate-400">wording not yet a template</span>}
        {r.kind === "template" && r.enabled === false && <span className="ml-2 text-slate-400">template disabled</span>}
        {r.automations?.length > 0 && <span className="ml-2">· fires on {r.automations.map((a) => `${a.event_type}${a.enabled ? "" : " (automation off)"}`).join(", ")}</span>}
      </div>
    </div>
    <div className="flex items-center gap-3">
      {r.kind === "template" && <SendSampleButton templateKey={r.key} />}
      <DeliveryBadge mode={r.delivery_mode} size="lg" testid={`delivery-mode-${r.key}`} />
      <button onClick={() => onFlip(r)} data-testid={`delivery-toggle-${r.key}`}
        className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${r.delivery_mode === "live" ? "border-amber-300 text-amber-800 hover:bg-amber-50" : "border-green-300 text-green-800 hover:bg-green-50"}`}>
        {r.delivery_mode === "live" ? "Switch to Test" : "Go Live…"}
      </button>
    </div>
  </div>
);

export const DeliveryPane = () => {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [pending, setPending] = useState(null);

  const load = useCallback(() => {
    api.get("/comms/delivery-modes").then(({ data: d }) => setData(d)).catch((e) => toast.error(formatApiError(e)));
    api.get("/comms/delivery-modes/events").then(({ data: d }) => setEvents(d)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const flip = async (note) => {
    const to = pending.delivery_mode === "live" ? "test" : "live";
    try {
      await api.patch(`/comms/delivery-modes/${pending.key}`, { delivery_mode: to, note });
      toast.success(`${pending.name || pending.key} is now ${to.toUpperCase()}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const used = new Set();
  const sections = GROUPS.map((g) => {
    const rows = data.templates.filter((r) => !used.has(r.key) && g.match(r));
    rows.forEach((r) => used.add(r.key));
    return { ...g, rows };
  }).filter((g) => g.rows.length);
  const liveCount = [...data.templates, ...data.direct].filter((r) => r.delivery_mode === "live").length;
  const goingLive = pending && pending.delivery_mode !== "live";

  return (
    <div className="space-y-5" data-testid="delivery-pane">
      <div className="p-4 rounded-lg border border-slate-200 bg-white text-sm text-slate-700">
        <div className="font-semibold text-[#0A2540]">Live / Test delivery, one email at a time</div>
        <p className="mt-1"><b>Test</b> emails go to <code data-testid="delivery-test-recipient">{data.test_recipient || "(no test inbox configured, so test emails are held)"}</code> with a [TEST →] banner. <b>Live</b> emails go to the real recipient. Every email starts in Test, and anything not listed here is always sent as Test. <span data-testid="delivery-live-count">{liveCount}</span> live right now.</p>
        <p className="mt-1 text-slate-500">Automation on/off still controls <i>whether</i> an email fires. This switch controls <i>who receives it</i>.</p>
      </div>
      <div className="p-4 rounded-lg border-2 border-green-600/40 bg-green-50" data-testid="delivery-live-summary">
        <div className="text-xs uppercase tracking-wider text-green-800 font-semibold mb-2">Live right now: these go to real recipients</div>
        {liveCount === 0 ? <div className="text-sm text-slate-700" data-testid="delivery-live-none">None. Every email goes to your test inbox.</div> : (
          <div className="flex flex-wrap gap-2">
            {[...data.templates, ...data.direct].filter((r) => r.delivery_mode === "live").map((r) => (
              <span key={r.key} className="inline-flex items-center gap-2 text-sm bg-white border border-green-600/40 rounded-md px-2.5 py-1" data-testid={`delivery-live-item-${r.key}`}>
                <DeliveryBadge mode="live" /> {r.name || r.key}
              </span>
            ))}
          </div>
        )}
      </div>
      {data.force_test && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-red-300 bg-red-50 text-sm text-red-800" data-testid="delivery-force-test-banner">
          <AlertTriangle className="w-4 h-4" /> EMAIL_FORCE_TEST_MODE is on in this environment, so every email is sent as Test no matter what's set below.
        </div>
      )}
      {sections.map((g) => (
        <div key={g.label} className="border border-slate-200 rounded-lg bg-white">
          <div className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500">{g.label}</div>
          {g.rows.map((r) => <Row key={r.key} r={r} onFlip={setPending} />)}
        </div>
      ))}
      <div className="border border-slate-200 rounded-lg bg-white" data-testid="delivery-direct-section">
        <div className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500">System emails (wording lives in code for now)</div>
        {data.direct.map((r) => <Row key={r.key} r={r} onFlip={setPending} />)}
      </div>
      <div className="border border-slate-200 rounded-lg bg-white" data-testid="delivery-history">
        <div className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500">Change history</div>
        {events.length === 0 ? <div className="px-4 py-3 text-sm text-slate-500 border-t border-slate-100">No changes yet.</div> : events.map((e) => (
          <div key={e.id} className="px-4 py-2 text-xs border-t border-slate-100 text-slate-700">
            {new Date(e.created_at).toLocaleString()} · <code>{e.key}</code> {e.from.toUpperCase()} → <b>{e.to.toUpperCase()}</b> · {e.by_email}{e.note ? ` — “${e.note}”` : ""}
          </div>
        ))}
      </div>

      <ConfirmDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)} testid="delivery-confirm-dialog"
        title={goingLive ? `Send "${pending?.name || pending?.key}" to real recipients?` : `Switch "${pending?.name || pending?.key}" back to Test?`}
        description={goingLive
          ? `From now on this email goes to the actual ${pending?.audience || "recipient"} instead of your test inbox. No other email changes.`
          : `This email will go to ${data.test_recipient || "the test inbox"} again.`}
        confirmLabel={goingLive ? "Go Live" : "Switch to Test"} danger={goingLive}
        requireNote={goingLive} notePlaceholder={goingLive ? "Why it's ready (required)" : "Optional note"}
        onConfirm={flip}>
        {goingLive && pending?.automations?.length > 0 && (
          <div className="text-xs text-slate-600">Fires on: {pending.automations.map((a) => `${a.event_type} → ${a.recipient_class}${a.enabled ? "" : " (automation currently off)"}`).join(" · ")}</div>
        )}
      </ConfirmDialog>
    </div>
  );
};
